import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { cloneDeep, find, isEmpty, isNil } from 'lodash';
import * as Sentry from '@sentry/browser';
import Cookies from 'js-cookie';

import { ComponentTierName, DeviceConnectivity, SensorsPackage, StationTier } from '@/constants/enums';
import { CookiesKey } from '@/constants/CookiesKey.enum';
import { MetricType } from '@/constants/metric-type.enum';
import { PIDMock } from '@/constants/PIDMock';
import { StationPressureType } from '@/constants/stationPressureType.enum';
import { initStationSocket } from '@/sockets/socket';
import epaxios from '@/utils/epaxios';
import LogErrorMessage from '@/utils/LogErrorMessage';
import { checkTierPermission } from '@/utils/util-functions';

import RootStore from '../RootStore';

import * as constants from './constants';
import { StationControlType } from '../Lifecycle/constants';
import { Device } from './constants';
import { SPECIFIC_POWER_THRESHOLD } from './StationStore.utils';

const loadingDelay = 1000;

export default class StationStore {
  rootStore: typeof RootStore;
  @observable stations = [];
  @observable currentStation: constants.Station = cloneDeep(constants.INIT_CURRENT_STATION);
  @observable isLoading = false;
  @observable isLoadingStageData = false;
  @observable isSidebarCollapsed = false;
  @observable isSidebarTransitioning = false;
  @observable stationStoreUserError = false;
  @observable isReady = false;
  @observable isLoadingDraftPid = false;
  @observable draftDevices = [];
  @observable assetsDevices: constants.AssetsSalesforceDTO[] = [];
  @observable isExistsCustomerSuccessInStation = false;
  @observable stationTier: StationTier = StationTier.Optimize;
  @observable isMonitorStation = false;
  @observable isMonitorStationTierForPeriodicReports: boolean | null = null;
  @observable stationControlType: StationControlType = StationControlType.Controlled;
  @observable packageSensors: SensorsPackage[] = [];
  @observable tierPaymentPlan: constants.EcolightOffering;
  @observable shouldUseEstimatedFlow: boolean;
  @observable isStationRequestCare = false;
  @observable customMonitoredParameters: constants.CustomMonitoredParameters;
  @observable isWasteWaterStation = false;
  @observable hasNewActionsFeature = false;
  private flowHighEfficiencyUpperRange = 6.7;
  private flowMidEfficiencyUpperRange = 7.4;
  @observable useMockData = false;

  @observable currentStationStage: constants.StationStageDto | undefined = undefined;
  @observable stages: constants.Stage[] = [];
  @observable unitOfMeasure = '';
  @observable mainFlowSensors: Device[] = [];
  @observable mainPressureSensors: Device[] = [];
  @observable kpiFlowSensors: Device[] = [];

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    reaction(
      () => this.rootStore.userStore.stations,
      stations => {
        if (isEmpty(stations)) return this.resetStations();

        this.stations = stations;
        this.isReady = true;
      },
    );

    reaction(
      () => this.currentStation,
      async (currentStation = {}) => {
        await this.getStationPID(currentStation?.id);
        await this.getNewActionsFeature();

        if (checkTierPermission(ComponentTierName.PaymentPlan, this) && this.useMockData) {
          await this.addStationMockData();
        }
      },
    );

    reaction(
      () => this.useMockData,
      async () => {
        if (checkTierPermission(ComponentTierName.PaymentPlan, this)) {
          await this.getStationPID(this.currentStation.id);
          this.useMockData && (await this.addStationMockData());
        }
      },
    );

    reaction(
      () => this.isSidebarCollapsed,
      () => (this.isSidebarTransitioning = true),
    );
  }

  async getStationPID(id) {
    this.draftDevices = [];
    await this.get(id);
  }

  @action addStationMockData = async () => {
    const oldStation = find(this.stations, { id: this.currentStation.id });
    this.isLoading = true;
    try {
      const newStation = { ...oldStation, ...PIDMock };
      this.updateStationKPIs(newStation);
      Object.assign(oldStation, newStation);
    } catch (e) {
      LogErrorMessage('Station load error. Server status code ', e);
      this.stationStoreUserError = true;
    } finally {
      setTimeout(() => {
        this.isLoading = false;
      }, loadingDelay);
    }
  };

  @action get = async (id: string) => {
    if (!id) return;
    const oldStation = find(this.stations, { id });
    try {
      this.isStationRequestCare = false;
      this.isLoading = true;
      this.currentStation.devices = [];
      let newStation: constants.Station = {} as constants.Station;
      const { data: response } = await epaxios.get(`/station/${id}/pid`);
      newStation = response;
      this.rootStore.pidEditStore.lastActivatedUserInfo = newStation.lastActivatedUserInfo;
      this.isWasteWaterStation = newStation.stationPressureType === StationPressureType.WasteWaterTreatment;
      this.shouldUseEstimatedFlow = newStation?.kpis.flow?.shouldUseEstimatedFlow ?? false;
      this.unitOfMeasure = newStation?.unitOfMeasure ?? 'metric';

      this.stationTier = !isNil(newStation?.stationTier)
        ? (newStation?.stationTier as StationTier)
        : StationTier.Optimize;
      this.isMonitorStation = newStation?.stationTier
        ? [StationTier.Monitor, StationTier.Asset].includes(newStation.stationTier as StationTier)
        : false;
      this.isMonitorStationTierForPeriodicReports = newStation?.stationTier === StationTier.Monitor;
      this.updateStationControlType(newStation?.stationControlType ?? StationControlType.Controlled);
      this.packageSensors = newStation?.sensorsPackage || [
        SensorsPackage.DewPoint,
        SensorsPackage.Flow,
        SensorsPackage.Power,
        SensorsPackage.Pressure,
      ];

      if ([SensorsPackage.Power, SensorsPackage.Flow].every(sensor => this.packageSensors.includes(sensor))) {
        this.packageSensors.push(SensorsPackage.SpecificPower);
      }

      const { data: assetsDevices } = await epaxios.get<constants.AssetsSalesforceDTO[]>(
        `/salesforce/salesforce-assets/station/${id}`,
      );
      this.assetsDevices = Array.isArray(assetsDevices) ? assetsDevices : [];

      const {
        data: { status },
      } = await epaxios.get<{ status: boolean }>(`/salesforce/salesforce-assets/exsits-cutomer-success/station/${id}`);
      this.isExistsCustomerSuccessInStation = status;

      // TO DO Implement Wastewater KPIs
      this.updateStationKPIs(newStation);
      Object.assign(oldStation, newStation);
      if (!checkTierPermission(ComponentTierName.PaymentPlan, this)) await initStationSocket(id);
    } catch (e) {
      this.assetsDevices = [];
      LogErrorMessage('Station load error. Server status code ', e);
      this.stationStoreUserError = true;
    } finally {
      setTimeout(() => {
        this.isLoading = false;
      }, loadingDelay);
    }
  };

  @action updateStationControlType = (controlType: StationControlType) => {
    this.stationControlType = controlType;
  };

  private updateStationKPIs = newStation => {
    Object.assign(newStation.kpis.pressure, {
      devices: () => this.getPressureDevices(this.currentStation),
      currentValue: this.getPressureBar(this.getPressureDevices(newStation)),
      targetValue: this.getTargetPressureBar(newStation.kpis.pressure),
    });
    Object.assign(newStation.kpis.flow, {
      devices: () => this.getFlowDevices(this.currentStation),
      currentValue: this.getFlowM3Min(this.getFlowDevices(newStation)),
    });
    Object.assign(newStation.kpis.power, {
      devices: () => this.getPowerDevices(this.currentStation),
      currentValue: this.getPowerKw(this.getPowerDevices(newStation)),
      flowHighEfficientRangeHigh: () => this.getFlowHighEfficientRangeHigh(),
      flowMidEfficientRangeHigh: () => this.getFlowMidEfficientRangeHigh(),
    });

    this.kpiFlowSensors = this.getFlowDevices(newStation);
    this.updateSpecificPower(newStation);
  };

  getPowerKw = (devices: { powerKw?: number; estimatedKw?: number; name?: string }[]) =>
    devices.map(d => d[MetricType.PowerKw] ?? d[MetricType.EstimatedKw] ?? 0).reduce((a, b) => a + b, 0);

  private getPressureBar = (devices: { pressureBar: number }[]) => {
    const totalPressureBar = devices.map(d => d.pressureBar).reduce((a, b) => a + b, 0);
    return totalPressureBar && devices.length ? totalPressureBar / devices.length : null;
  };

  getFlowM3Min = (devices: { flowM3Min?: number; estimatedFlowM3Min?: number }[]) =>
    devices
      .map(d => {
        const value = this.shouldUseEstimatedFlow ? d.estimatedFlowM3Min : d.flowM3Min;
        return typeof value === 'number' && !isNaN(value) ? value : 0; // ensure we only sum valid numbers
      })
      .reduce((a, b) => a + b, 0);

  private getFlowMidEfficientRangeHigh = () =>
    this.currentStation.kpis.flow.currentValue
      ? StationStore.toFixedNumber(this.currentStation.kpis.flow.currentValue * this.flowMidEfficiencyUpperRange, 3)
      : null;

  private getFlowHighEfficientRangeHigh = () =>
    this.currentStation.kpis.flow.currentValue
      ? StationStore.toFixedNumber(this.currentStation.kpis.flow.currentValue * this.flowHighEfficiencyUpperRange, 3)
      : null;

  getTargetPressureBar = (pressure = this.currentStation.kpis.pressure) =>
    pressure.targetMinThreshold && pressure.targetMaxThreshold
      ? StationStore.toFixedNumber((pressure.targetMinThreshold + pressure.targetMaxThreshold) / 2, 1)
      : null;

  private getPowerDevices = station =>
    station.devices.filter(device => station.kpis.power.powerDevices.includes(device.deviceId));

  private getFlowDevices = station =>
    station.devices.filter(device => station.kpis.flow.flowDevices.includes(device.deviceId));

  private getPressureDevices = station => {
    const connectedDevices = station.devices.filter(device => device.connectivity === DeviceConnectivity.Connected);
    let devices = connectedDevices.filter(device => station.kpis.pressure.pressureDevices.includes(device.deviceId));
    if (devices.length === 0 && station.kpis.pressure.backupPressureDevices) {
      devices = connectedDevices.filter(device =>
        station.kpis.pressure.backupPressureDevices.includes(device.deviceId),
      );
    }
    return devices;
  };

  private getMainDevices = (deviceIds: string[]): Device[] => {
    if (!deviceIds) return [];
    return this.currentStation.devices.filter(device => deviceIds.includes(device.deviceId));
  };

  @action getNewActionsFeature = async () => {
    if (!this.currentStation.id) return;

    try {
      const { data } = await epaxios.get(`/station/${this.currentStation.id}/new-actions-feature`);
      this.hasNewActionsFeature = data.newActionsFeature;
    } catch (e) {
      LogErrorMessage('Error getting new actions feature', e);
    }
  };

  getDeviceById = deviceId => {
    return find(this.currentStation.devices, { deviceId });
  };

  getOnDevicesForDevice = deviceId => {
    const fullDevice = this.getDeviceById(deviceId);
    const onDevices = this.currentStation.devices?.filter(device => fullDevice?.onDevices?.includes(device.deviceId));
    return onDevices || [];
  };

  @action updateDeviceFromServer = newDevice => {
    if (!newDevice) return;
    const oldDevice = find(this.currentStation.devices, { deviceId: newDevice.deviceId });
    if (!oldDevice) return;
    Object.assign(oldDevice, newDevice);
    oldDevice.additionalDetails?.forEach(detail => {
      const newValue = newDevice[detail.metric];
      if (newValue) {
        detail.value = newValue;
      }
    });
    this.updateKPIs(newDevice);
  };

  @action updateKPISFromServer = newKPIS => {
    if (!newKPIS) return;
    const oldKPIS = this.currentStation.kpis;
    if (!oldKPIS) return;
    Object.assign(oldKPIS, {
      ...newKPIS,
      specificPower: newKPIS.specificPower >= SPECIFIC_POWER_THRESHOLD ? 'N/A' : newKPIS.specificPower,
    });
  };

  @action setUseMockData(newValue) {
    this.useMockData = newValue;
  }

  @action setCurrentStation = id => {
    const newCurrentStation = find(this.stations, { id });
    if (!newCurrentStation) return;
    this.currentStation = newCurrentStation;
  };

  @action resetStations = () => {
    this.stations = [];
    this.currentStation = cloneDeep(constants.INIT_CURRENT_STATION);
    this.isReady = false;
    this.resetStationStoreUserError();
  };

  @action resetStationStoreUserError = () => {
    this.stationStoreUserError = false;
  };

  @action updateKPIs = deviceUpdate => {
    const device = this.getDeviceById(deviceUpdate.deviceId);
    if (device.connectivity === DeviceConnectivity.Connected) {
      this.updatePowerKwKPI(deviceUpdate);
      this.updatePressureBarKPI(deviceUpdate);
      this.updateFlowM3MinKPI(deviceUpdate);
      this.updateSpecificPower(this.currentStation);
    }
  };

  @action updateRequestTier = async (queryParam?: constants.QueryRequestEmailTierDto) => {
    const EMAIL_BLOCK_EXPIRATION_DAYS = 1;
    const emailAlreadySent = Cookies.get(CookiesKey.blockRequestTierEmail);

    if (!emailAlreadySent) {
      const params = Object.fromEntries(Object.entries(queryParam)?.filter(([, value]) => value));

      await epaxios.get(`/salesforce/salesforce-assets/tier/signup/${this.currentStation.id}`, {
        params,
      });

      Cookies.set(CookiesKey.blockRequestTierEmail, true, { expires: EMAIL_BLOCK_EXPIRATION_DAYS });
    }
  };

  @action signUpPackageCare = async () => {
    if (!this.currentStation.id) return;

    try {
      await epaxios.get(`/salesforce/salesforce-assets/signup/${this.currentStation.id}`);
      this.isStationRequestCare = true;
    } catch (e) {
      Sentry.captureException(new Error(`Error when signing up Package Care: ${e}`));
    }
  };

  private updateSpecificPower = station => {
    const flowValue = station.kpis.flow.currentValue;
    const powerValue = station.kpis.power.currentValue;
    const specificPower = powerValue / flowValue;
    if (!station.kpis.specificPower && flowValue && powerValue) {
      Object.assign(station.kpis, {
        specificPower: specificPower >= SPECIFIC_POWER_THRESHOLD ? 'N/A' : specificPower,
      });
    }
  };

  @action updateFlowM3MinKPI = deviceUpdate => {
    if (deviceUpdate[MetricType.FlowM3Min] ?? deviceUpdate[MetricType.EstimatedFlowM3Min]) {
      Object.assign(this.currentStation.kpis.flow, {
        currentValue: this.getFlowM3Min(this.currentStation.kpis.flow.devices()),
      });
    }
  };

  @action updatePressureBarKPI = deviceUpdate => {
    if (deviceUpdate.pressureBar) {
      const devices = this.currentStation.kpis.pressure.devices();
      Object.assign(this.currentStation.kpis.pressure, {
        currentValue: this.getPressureBar(devices),
      });
    }
  };

  @action updatePowerKwKPI = deviceUpdate => {
    if (deviceUpdate[MetricType.PowerKw] ?? deviceUpdate[MetricType.EstimatedKw]) {
      Object.assign(this.currentStation.kpis.power, {
        currentValue: this.getPowerKw(this.currentStation.kpis.power.devices()),
      });
    }
  };

  @action toggleSidebar = () => {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
  };

  @action getCustomMonitoredParameters = async () => {
    const {
      data,
    }: {
      data: constants.CustomMonitoredParameters;
    } = await epaxios.get(`/station/${this.currentStation.id}/custom-monitored-parameters`);
    this.customMonitoredParameters = data;
  };

  private static toFixedNumber(value: number, fractionDigits: number): number {
    return Number(value.toFixed(fractionDigits));
  }

  @action getTierOffering = async () => {
    if (!this.currentStation.id) return;

    try {
      const { data } = await epaxios.get(`value-dashboard/tier-offering/station/${this.currentStation.id}/all-tiers`);
      this.tierPaymentPlan = data;
    } catch (e) {
      Sentry.captureException(new Error(`Error when getting Tier Offering: ${e}`));
    }
  };

  getStationById = (id: string) => find(this.stations, { id });

  @action setCurrentStationStage(stationStage: constants.StationStageDto) {
    this.currentStationStage = stationStage;
  }

  @action async updateStationStage(stageId: number, stationId: string) {
    const selectedStage = this.stages.find(stage => stage.id === stageId);

    if (!selectedStage) return;

    try {
      const stationStage = await epaxios.post(`/station-stage-admin/station-stages`, {
        stationId,
        stageId: selectedStage.id,
      });

      runInAction(() => {
        this.currentStationStage = { ...stationStage.data, stage: selectedStage };
      });
      this.rootStore.lifecycleStore.reset();
    } catch (error) {
      console.error('Failed to update station stage', error);
      throw error;
    }
  }

  @action async fetchStationData(stationId: string) {
    this.isLoadingStageData = true;
    try {
      const [stagesRes, currentStageRes] = await Promise.all([
        epaxios.get(`/station-stage-admin/stages`),
        epaxios.get(`/station-mgmt/station/${stationId}`),
      ]);

      runInAction(() => {
        this.stages = stagesRes.data.sort((a: constants.Stage, b: constants.Stage) => a.order - b.order);
        this.currentStationStage = currentStageRes.data.stationStage;
        this.mainFlowSensors = this.getMainDevices(currentStageRes.data.attributes?.flowDevices);
        this.mainPressureSensors = this.getMainDevices(currentStageRes.data.attributes?.pressureDevices);
      });
    } catch (error) {
      console.error('Failed to fetch station data:', error);
      this.stationStoreUserError = true;
    } finally {
      runInAction(() => {
        this.isLoadingStageData = false;
      });
    }
  }

  isFavouriteStation = (stationId: string): boolean => {
    return this.stations.find(station => station.id === stationId)?.isFavorite;
  };

  toggleFavouriteStation = async (stationId: string): Promise<void> => {
    try {
      this.stations = this.stations.map(station => ({
        ...station,
        isFavorite: station.id === stationId ? !station.isFavorite : station.isFavorite,
      }));
      await this.rootStore.usersStore.update(
        this.rootStore.userStore.user.id,
        {
          userFavoriteStations: this.stations
            .filter(station => station.isFavorite)
            .map(station => ({
              station: { id: station.id },
            })),
        },
        null,
        null,
      );
    } catch (error) {
      console.error('Error toggling favourite station:', error);
    }
  };
}
